import React, { useEffect, useState } from 'react';
import { useOrion } from '../context/OrionContext';
import Icon from './Icon';
import ComboBusca from './ComboBusca';
import CampoAnotacoes from './CampoAnotacoes';
import { PanelHead } from './ui';
import {
  codigoSolicitanteDoTicket, nomeSolicitante, ticketDaFila,
  ticketsEmAtendimentoDoOperador,
} from '../services/mysuiteApi';
import { criticidadesDaOcorrencia, ordenarPorNome } from '../services/listasApi';
import { encontrarMelhorMatch } from '../services/texto';
import {
  avisarComercialNoTeams, formalizarNovoTicket, inserirTicketNovo, notificarEmAtendimento,
} from '../services/helpdeskApi';
import { MAPA_CATEGORIAS } from '../config/ambiente';
import {
  statusEOperador, resolverSetor, resolverCategoria, resolverPrioridade,
  motivoParaNaoCriar,
} from '../services/novoTicketService';
import { TIPOS_ATENDIMENTO } from '../services/formalizacaoService';

// ─────────────────────────────────────────────────────────────────────────────
// Criar um ticket NOVO durante o atendimento.
//
// O cliente liga por um problema e, no meio da conversa, aparece outro. Sem
// isto, abrir o segundo ticket significa sair do Orion, achar o cliente e
// redigitar tudo — e o segundo problema costuma ficar sem registro.
//
// O que é HERDADO do ticket de origem (e por isso aparece só para conferência):
// solicitante, CNPJ e setor. O que é NOVO: título, descrição e classificação.
//
// A herança do solicitante tem três níveis, do mais confiável ao menos:
//   1. a fila de pendentes, que traz o código junto;
//   2. o que já foi lido do ticket de origem;
//   3. uma consulta à API do ticket de origem.
// Sem código, sobra o CNPJ — e aí quem resolve é o n8n, com a ressalva de que
// pode cair em outro contato do mesmo cliente. Daí as três tentativas antes.
// ─────────────────────────────────────────────────────────────────────────────

export default function NovoTicket({
  ticketOrigem,
  ticketInfo,
  solicitante,
  listas,
  aoCriado,        // (numero) => void
  aoFechar,
}) {
  const { operador, avisar } = useOrion();

  const [titulo, setTitulo] = useState(ticketInfo?.titulo || '');
  const [interacao, setInteracao] = useState('');
  // Áudio do relato — gravado ou anexado. A IA transcreve ao formalizar.
  const [audio, setAudio] = useState(null);
  const [tipo, setTipo] = useState('Em atendimento');

  // Combos: cada um guarda o texto visível e o código que vai para o MySuite.
  const [ocorrencia, setOcorrencia] = useState({ codigo: '', nome: '' });
  const [criticidade, setCriticidade] = useState({ codigo: '', nome: '' });
  const [classificacao, setClassificacao] = useState({ codigo: '', nome: '' });
  const [categoria, setCategoria] = useState('');

  const [codigoSolic, setCodigoSolic] = useState(null);
  const [nomeSolic, setNomeSolic] = useState(solicitante || '');
  const [resolvendo, setResolvendo] = useState(true);

  const [formalizando, setFormalizando] = useState(false);
  // O que a IA propôs para este ticket novo: assunto e classificação. Fica
  // visível até o operador aplicar ou fechar o modal — no 1.4.1 é a caixa
  // "Sugestão da IA".
  const [sugestao, setSugestao] = useState(null);
  const [criando, setCriando] = useState(false);
  const [criado, setCriado] = useState(null);

  // Saída para quando a resolução automática do solicitante não funciona.
  const [codigoManual, setCodigoManual] = useState('');
  const [conferindoCodigo, setConferindoCodigo] = useState(false);
  const [erroCodigoManual, setErroCodigoManual] = useState('');

  // ── Herança do solicitante ────────────────────────────────────────────────
  useEffect(() => {
    let vivo = true;
    (async () => {
      setResolvendo(true);
      try {
        // 1) a fila de pendentes já traz o código do solicitante.
        const pend = await ticketDaFila(ticketOrigem);
        let cod = parseInt(pend?.codigoUsuarioCliente, 10) || null;

        // 2) o que já foi lido do ticket de origem.
        if (!cod) cod = parseInt(ticketInfo?.codigoUsuarioCliente, 10) || null;

        // 3) consulta direta ao ticket de origem.
        if (!cod) cod = await codigoSolicitanteDoTicket(ticketOrigem);

        if (!vivo) return;
        setCodigoSolic(cod || null);

        // O nome vem depois e só é aplicado se o código ainda for o mesmo —
        // senão sobrescreveria uma resolução mais nova.
        if (cod && !solicitante) {
          const nome = await nomeSolicitante(cod);
          if (vivo && nome) setNomeSolic(nome);
        }
      } catch (e) {
        // Não é fatal: a tela pede o código ao operador. Mas registra, senão
        // não há como descobrir POR QUE a resolução falhou — foi o que manteve
        // este defeito sem diagnóstico por tanto tempo.
        console.warn(`[Orion] Não resolvi o solicitante do ticket ${ticketOrigem}:`, e.message);
      } finally {
        if (vivo) setResolvendo(false);
      }
    })();
    return () => { vivo = false; };
  }, [ticketOrigem, ticketInfo, solicitante]);

  /**
   * Confere o código digitado antes de aceitá-lo.
   *
   * Exigir que o código EXISTA é o que separa esta saída de simplesmente voltar
   * ao problema antigo: um número digitado errado abriria o ticket para outra
   * pessoa do mesmo jeito, só que agora por engano de digitação.
   */
  const confirmarCodigoManual = async () => {
    const cod = parseInt(codigoManual, 10);
    if (!cod) { setErroCodigoManual('Informe um código numérico.'); return; }

    setConferindoCodigo(true);
    setErroCodigoManual('');
    try {
      const nome = await nomeSolicitante(cod);
      if (nome) {
        setCodigoSolic(cod);
        setNomeSolic(nome);
        setErroCodigoManual('');
      } else {
        setErroCodigoManual(`Não encontrei um solicitante com o código ${cod}. `
          + 'Confira no MySuite, na aba do ticket de origem.');
      }
    } catch (e) {
      setErroCodigoManual(`Não deu para conferir o código: ${e.message}`);
    } finally {
      setConferindoCodigo(false);
    }
  };

  // Pré-preenche a categoria com a do ticket de origem.
  useEffect(() => {
    setCategoria(String(resolverCategoria({ ticketInfo })));
  }, [ticketInfo]);

  // Cascata ocorrência → criticidade, a mesma da tela principal.
  const opcoesCriticidade = criticidadesDaOcorrencia(listas, ocorrencia.codigo);
  useEffect(() => {
    if (opcoesCriticidade.lista.length === 1) {
      const unica = opcoesCriticidade.lista[0];
      setCriticidade({ codigo: String(unica.codigo), nome: unica.nome });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ocorrencia.codigo]);

  // ── Ações ─────────────────────────────────────────────────────────────────

  const formalizar = async () => {
    // Com áudio o texto é opcional: a transcrição é a fonte, como no atendimento.
    if (!interacao.trim() && !audio) {
      avisar('Escreva o que aconteceu ou grave um áudio antes de formalizar.', { tipo: 'erro' });
      return;
    }
    setFormalizando(true);
    try {
      // Duas chamadas, como no 1.4.1: o texto e, em seguida, a classificação
      // sugerida. A sugestão fica à vista; aplicar é decisão do operador.
      const { texto, assunto, sugestao: sug } = await formalizarNovoTicket({
        anotacoes: interacao.trim(),
        ticketOrigem,
        solicitante: nomeSolic,
        operador,
        audio,
      });
      if (texto) {
        setInteracao(texto);
        // O áudio já virou texto: mandar de novo numa segunda formalização
        // juntaria a transcrição duas vezes.
        setAudio(null);
        avisar(audio ? 'Áudio transcrito e texto formalizado.' : 'Texto formalizado.', { tipo: 'ok' });
      } else {
        avisar('A IA não devolveu texto; o que você escreveu foi mantido.', { tipo: 'info' });
      }
      if (assunto || sug) setSugestao({ assunto, ...(sug || {}) });
    } catch (err) {
      // O texto original continua ali — falha da IA não pode apagar o que foi
      // escrito nem impedir a criação do ticket.
      avisar(`Não foi possível formalizar: ${err.message}`, { tipo: 'erro' });
    } finally {
      setFormalizando(false);
    }
  };

  /**
   * Aplica a sugestão da IA: assunto no título e os nomes nos combos.
   *
   * O casamento é pelo NOME, porque é isso que a IA devolve — o mesmo
   * `encontrarMelhorMatch` que a revisão do atendimento usa.
   */
  const aplicarSugestao = () => {
    if (!sugestao) return;
    if (sugestao.assunto && !titulo.trim()) setTitulo(sugestao.assunto);

    const casar = (texto, lista) => {
      if (!texto) return null;
      const m = encontrarMelhorMatch(texto, (lista || []).map(i => ({ valor: String(i.codigo), texto: i.nome })));
      if (!m) return null;
      const achado = (lista || []).find(i => String(i.codigo) === m.valor);
      return achado ? { codigo: String(achado.codigo), nome: achado.nome } : null;
    };

    const oc = casar(sugestao.ocorrencia, listas?.ocorrencias);
    if (oc) setOcorrencia(oc);
    const cr = casar(sugestao.criticidade, listas?.criticidades);
    if (cr) setCriticidade(cr);
    const cl = casar(sugestao.classificacao, listas?.classificacoes);
    if (cl) setClassificacao(cl);

    avisar('Recomendação da IA aplicada.', { tipo: 'ok' });
    setSugestao(null);
  };

  const criar = async () => {
    const cnpj = ticketInfo?.cnpjCliente || ticketInfo?.cnpj || '';
    const impedimento = motivoParaNaoCriar({
      titulo, interacao, codigoUsuarioCliente: codigoSolic, cnpjCliente: cnpj,
    });
    if (impedimento) { avisar(impedimento, { tipo: 'erro' }); return; }
    // O MySuite recebe só texto: um áudio ainda não transcrito ficaria de fora
    // do ticket sem ninguém perceber.
    if (audio) {
      avisar('Há um áudio ainda não transcrito. Clique em "Formalizar com IA" ou remova o áudio antes de criar.', { tipo: 'erro' });
      return;
    }

    setCriando(true);
    try {
      const { codigoStatus, codigoOperador } = statusEOperador(tipo, {
        codigoOperadorLogado: operador?.codigo || 0,
      });

      const { numero } = await inserirTicketNovo({
        titulo: titulo.trim(),
        interacao: interacao.trim(),
        codigoStatus,
        codigoOperador,
        // Sem `|| 0`: era esse zero que fazia o MySuite escolher o
        // solicitante pelo CNPJ. motivoParaNaoCriar já garante que há código
        // aqui — o valor chega resolvido ou conferido pelo operador.
        codigoUsuarioCliente: codigoSolic,
        cnpjCliente: cnpj,
        codigoSetor: resolverSetor({ ticketInfo }),
        codigoCategoria: parseInt(categoria, 10) || resolverCategoria({ ticketInfo }),
        // Só os CÓDIGOS: no 1.4.1 o fluxo de ticket novo não recebe os nomes
        // de ocorrência, criticidade e classificação (quem os manda é o de
        // criação em massa). Campo a mais aqui muda o que o n8n processa.
        codigoOcorrencia: parseInt(ocorrencia.codigo, 10) || 0,
        codigoCriticidade: parseInt(criticidade.codigo, 10) || 0,
        codigoClassificacao: parseInt(classificacao.codigo, 10) || 0,
        prioridade: resolverPrioridade({ ticketInfo }),
        // Ticket novo nasce sem previsão.
        dataPrevisao: '0000-00-00',
        horaPrevisao: '00:00:00',
        // Rastreabilidade: de qual atendimento este ticket saiu.
        tipoAtendimento: tipo,
        // Número, como no 1.4.1 (parseInt ou null) — não a string da tela.
        ticketOrigem: parseInt(ticketOrigem, 10) || null,
        operadorNome: operador?.nome || '',
        operadorEmail: operador?.email || '',
      });

      // O Comercial só é avisado quando o número veio: "encaminhar o ticket:
      // null" não ajudaria ninguém no canal do Teams.
      if (tipo === 'Comercial') {
        if (numero) avisarComercialNoTeams([numero]);
        else console.warn('[Orion] Ticket comercial criado sem número na resposta; Teams não avisado.');
      }

      // Criar um ticket é atuar: o n8n recebe a fila do operador sem ele.
      // Mesmo ponto do 1.4.1 (logo depois da criação), e igualmente sem travar
      // a tela se falhar.
      ticketsEmAtendimentoDoOperador(operador?.codigo)
        .then(lista => notificarEmAtendimento({
          ticketAtuado: numero,
          tickets: lista,
          operadorNome: operador?.nome || '',
          operadorEmail: operador?.email || '',
          codigoOperador: operador?.codigo ?? null,
          solicitante: nomeSolic,
        }))
        .catch(e => console.warn('[Orion] Em atendimento:', e.message));

      setCriado(numero);
      avisar(numero ? `Ticket ${numero} criado.` : 'Ticket criado (o fluxo não devolveu o número).',
        { tipo: 'ok' });
    } catch (err) {
      avisar(`Não foi possível criar o ticket: ${err.message}`, { tipo: 'erro' });
    } finally {
      setCriando(false);
    }
  };

  const ocupado = formalizando || criando;

  return (
    <div className="drawer-overlay" onClick={() => !ocupado && aoFechar()}>
      <div className="modal-nx modal-largo" onClick={e => e.stopPropagation()}>
        <div className="drawer-head">
          <div className="drawer-title">
            Criar ticket novo
            <span className="drawer-sub">a partir do atendimento {ticketOrigem}</span>
          </div>
          <button className="drawer-close" onClick={aoFechar} disabled={ocupado}>
            <Icon name="x" size={15} />
          </button>
        </div>

        <div className="drawer-body">
          {criado ? (
            // Depois de criado, a única coisa que interessa é o número — e
            // poder ir para ele sem procurar.
            <div className="panel panel-dest d-ia" style={{ marginBottom: 0 }}>
              <PanelHead titulo="Ticket criado" sub={criado ? `nº ${criado}` : ''} />
              <div className="campo-dica" style={{ marginBottom: 12 }}>
                {criado
                  ? 'Abra o ticket novo para continuar o atendimento por ele.'
                  : 'O ticket foi criado, mas o fluxo não devolveu o número. Confira no MySuite.'}
              </div>
              {criado && (
                <button className="btn btn-primary btn-sm" onClick={() => aoCriado(criado)}>
                  <Icon name="ticket" size={13} /> Atender o ticket {criado}
                </button>
              )}
            </div>
          ) : (
            <>
              {/* ── Herdado: só para conferência ── */}
              <div className="panel panel-dest d-atual">
                <PanelHead titulo="Herdado do atendimento" sub="não é editável" />
                <div className="sol-body">
                  <div>
                    <div className="fld-label">Solicitante</div>
                    <div className="fld-value">
                      {resolvendo ? 'resolvendo…' : (nomeSolic || '—')}
                      {!resolvendo && codigoSolic && <span className="sol-id"> (cód. {codigoSolic})</span>}
                      {!resolvendo && !codigoSolic && (
                        <span className="sol-id"> (não identificado)</span>
                      )}
                    </div>
                  </div>
                  <div>
                    <div className="fld-label">CNPJ do cliente</div>
                    <div className="fld-value">{ticketInfo?.cnpjCliente || ticketInfo?.cnpj || '—'}</div>
                  </div>
                  <div>
                    <div className="fld-label">Setor</div>
                    <div className="fld-value">{ticketInfo?.setor || `código ${resolverSetor({ ticketInfo })}`}</div>
                  </div>
                </div>
                {/* Quando a resolução automática falha, o operador informa o
                    código. Antes o ticket era criado assim mesmo e o MySuite
                    escolhia um contato pelo CNPJ — que quase nunca era o do
                    ticket de origem. Era a causa do solicitante trocado. */}
                {!resolvendo && !codigoSolic && (
                  <div className="panel-faixa d-risco" style={{ marginTop: 10 }}>
                    <div className="campo-dica" style={{ marginBottom: 8 }}>
                      <strong>O solicitante não foi identificado automaticamente.</strong>{' '}
                      Informe o código para o ticket novo ficar com a mesma pessoa
                      do ticket {ticketOrigem}. Sem isso não dá para criar: pelo
                      CNPJ, o chamado abriria para outro contato do cliente.
                    </div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <input
                        type="text"
                        inputMode="numeric"
                        value={codigoManual}
                        onChange={e => setCodigoManual(e.target.value.replace(/\D/g, ''))}
                        placeholder="Código do solicitante"
                        style={{ maxWidth: 200 }}
                      />
                      <button
                        className="btn btn-sm"
                        onClick={confirmarCodigoManual}
                        disabled={!codigoManual.trim() || conferindoCodigo}
                      >
                        {conferindoCodigo ? 'conferindo…' : 'Conferir'}
                      </button>
                    </div>
                    {erroCodigoManual && (
                      <div className="campo-dica" style={{ marginTop: 8 }}>{erroCodigoManual}</div>
                    )}
                  </div>
                )}
              </div>

              {/* ── O que é novo ── */}
              <div className="panel">
                <PanelHead titulo="O ticket novo" />

                <label className="field-label">Título</label>
                <input
                  type="text"
                  value={titulo}
                  onChange={e => setTitulo(e.target.value)}
                  placeholder="Assunto da nova situação"
                />

                <label className="field-label" style={{ marginTop: 14 }}>Tipo de atendimento</label>
                <select value={tipo} onChange={e => setTipo(e.target.value)}>
                  <option value="Em atendimento">Em atendimento</option>
                  {TIPOS_ATENDIMENTO.map(t => (
                    <option key={t.valor} value={t.valor}>{t.rotulo}</option>
                  ))}
                </select>

                <label className="field-label" style={{ marginTop: 14 }}>O que aconteceu</label>
                {/* O mesmo campo das anotações do atendimento, com o gravador
                    no canto — sem imagens: o ticket novo não leva anexo. */}
                <CampoAnotacoes
                  valor={interacao}
                  aoMudar={setInteracao}
                  audio={audio}
                  aoMudarAudio={setAudio}
                  aoAvisar={m => avisar(m, { tipo: 'erro' })}
                  desabilitado={ocupado}
                  placeholder="Descreva a nova situação, ou grave um áudio; a IA transcreve e formaliza"
                />
                {audio && (
                  <div className="campo-dica" style={{ marginTop: 6 }}>
                    Clique em <b>Formalizar com IA</b> para transcrever o áudio — o texto
                    formalizado entra no campo acima para você revisar antes de criar.
                  </div>
                )}
                {/* Verde: é proposta da IA, não o que já está gravado — a mesma
                    separação de cor da revisão do atendimento. */}
                {sugestao && (
                  <div className="panel panel-dest d-ia" style={{ marginTop: 14 }}>
                    <PanelHead titulo="Sugestão da IA" sub="aplique se fizer sentido" />
                    <div className="sol-body">
                      <div>
                        <div className="fld-label">Assunto</div>
                        <div className="fld-value">{sugestao.assunto || '—'}</div>
                      </div>
                      <div>
                        <div className="fld-label">Classificação</div>
                        <div className="fld-value">{sugestao.classificacao || '—'}</div>
                      </div>
                      <div>
                        <div className="fld-label">Ocorrência</div>
                        <div className="fld-value">{sugestao.ocorrencia || '—'}</div>
                      </div>
                      <div>
                        <div className="fld-label">Criticidade</div>
                        <div className="fld-value">{sugestao.criticidade || '—'}</div>
                      </div>
                    </div>
                    <div className="sol-actions" style={{ marginTop: 12 }}>
                      <button className="btn btn-primary btn-sm" onClick={aplicarSugestao}>
                        <Icon name="check" size={12} /> Aplicar recomendação
                      </button>
                    </div>
                  </div>
                )}

                <div className="acoes-fim" style={{ margin: '10px 0 0', justifyContent: 'flex-start' }}>
                  <button className="btn btn-ghost btn-sm" onClick={formalizar} disabled={ocupado}>
                    {formalizando
                      ? <><Icon name="rotate-cw" size={13} className="orion-spin" /> {audio ? 'Transcrevendo e formalizando…' : 'Formalizando…'}</>
                      : <><Icon name="zap" size={13} /> Formalizar com IA</>}
                  </button>
                </div>
              </div>

              {/* ── Classificação: combos com busca ── */}
              <div className="panel">
                <PanelHead titulo="Classificação" sub="digite para buscar" />

                <label className="field-label">Classificação</label>
                <ComboBusca
                  itens={ordenarPorNome(listas.classificacoes)}
                  valor={classificacao.nome}
                  codigo={classificacao.codigo}
                  aoEscolher={(codigo, nome) => setClassificacao({ codigo, nome })}
                  desabilitado={ocupado}
                />

                <div className="detail-grid" style={{ marginTop: 14 }}>
                  <div>
                    <label className="field-label">Ocorrência</label>
                    <ComboBusca
                      itens={ordenarPorNome(listas.ocorrencias)}
                      valor={ocorrencia.nome}
                      codigo={ocorrencia.codigo}
                      aoEscolher={(codigo, nome) => {
                        setOcorrencia({ codigo, nome });
                        setCriticidade({ codigo: '', nome: '' });
                      }}
                      desabilitado={ocupado}
                    />
                  </div>
                  <div>
                    <label className="field-label">Criticidade</label>
                    <ComboBusca
                      itens={opcoesCriticidade.lista}
                      valor={criticidade.nome}
                      codigo={criticidade.codigo}
                      aoEscolher={(codigo, nome) => setCriticidade({ codigo, nome })}
                      placeholder={opcoesCriticidade.mensagem || 'Digite para buscar…'}
                      desabilitado={ocupado || !opcoesCriticidade.lista.length}
                    />
                  </div>
                  <div>
                    <label className="field-label">Categoria (produto)</label>
                    <select value={categoria} onChange={e => setCategoria(e.target.value)} disabled={ocupado}>
                      {Object.entries(MAPA_CATEGORIAS)
                        .sort((a, b) => a[0].localeCompare(b[0], 'pt-BR'))
                        .map(([nome, cod]) => <option key={cod} value={cod}>{nome}</option>)}
                    </select>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        <div className="drawer-foot">
          <button className="btn btn-ghost" onClick={aoFechar} disabled={ocupado}>
            {criado ? 'Fechar' : 'Cancelar'}
          </button>
          {!criado && (
            <button className="btn btn-primary" onClick={criar} disabled={ocupado}>
              {criando
                ? <><Icon name="rotate-cw" size={14} className="orion-spin" /> Criando…</>
                : <><Icon name="plus" size={14} /> Criar ticket</>}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
